// What each send of a quote looked like to the client (Stephanie 2026-10-27:
// "since we dont have pdfs, i cant see what i sent last"). Read-only: the
// copies are saved by the server on every send (services/sent-copies.ts).
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api } from "@/lib/api";

interface CopyLine {
  id: string;
  serviceType?: string;
  description?: string | null;
  productSnapshot?: { name?: string } | null;
  unitPrice?: string | null;
  quantityTotal?: number;
  variants?: Array<{ color?: string | null; size?: string | null; quantity: number; unitPrice?: string | null }>;
  decorations?: Array<{ location?: string | null; charges?: Array<{ chargeType?: string; note?: string | null; amount: string; billingModel?: string; quantity?: number | null }> }>;
}
interface SentCopy {
  sentAt: string;
  sentTo: string[];
  sentByName: string | null;
  lines: CopyLine[];
  totals: { subtotal: string; tax: string; total: string } | null;
  inHandsDate: string | null;
  hidePricing: boolean;
}

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);

const when = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

export function SentCopiesModal(props: { orderId: string; currency: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["order", props.orderId, "sent-copies"],
    queryFn: () => api<{ copies: Array<{ id: string; createdAt: string; copy: SentCopy }> }>(`/orders/${props.orderId}/sent-copies`),
  });
  const copies = q.data?.copies ?? [];
  const [openId, setOpenId] = useState<string | null>(null);
  const shown = copies.find((c) => c.id === (openId ?? copies[0]?.id));
  const money = (v: string | null | undefined) => (v == null || v === "" ? "—" : formatCurrency(Number(v), props.currency));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 px-4" onClick={(e) => { if (e.target === e.currentTarget) props.onClose(); }} role="dialog" aria-modal="true" aria-label="Sent copies">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-brown-100 bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-brown-50 px-6 py-4">
          <div>
            <h2 className="text-2xl font-semibold text-brown-500">Sent copies</h2>
            <p className="mt-1 text-sm text-brown-300">Exactly what the client saw each time this quote was sent.</p>
          </div>
          <button type="button" onClick={props.onClose} aria-label="Close" className="text-brown-300 hover:text-brown-500"><X className="h-5 w-5" /></button>
        </div>
        {q.isLoading ? (
          <p className="px-6 py-5 text-sm text-brown-300">Loading…</p>
        ) : q.isError ? (
          <p className="px-6 py-5 text-sm text-rust-600">Couldn&apos;t load the sent copies. Try again.</p>
        ) : copies.length === 0 ? (
          <p className="px-6 py-5 text-sm text-brown-300">No saved copies yet — a copy is kept every time you send this quote from now on.</p>
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-[220px_minmax(0,1fr)]">
            <ul className="overflow-y-auto border-r border-brown-50 py-2">
              {copies.map((c, i) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setOpenId(c.id)}
                    className={`block w-full px-4 py-2.5 text-left text-sm ${shown?.id === c.id ? "bg-cream-100 font-semibold text-brown-500" : "text-brown-400 hover:bg-cream-50"}`}
                  >
                    {when(c.copy.sentAt)}
                    <span className="block text-xs font-normal text-brown-300">
                      {i === 0 ? "Latest · " : ""}{c.copy.totals ? money(c.copy.totals.total) : "prices hidden"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {shown && (
              <div className="overflow-y-auto px-6 py-4 text-sm text-brown-500">
                <p className="text-xs text-brown-300">
                  Sent {when(shown.copy.sentAt)}{shown.copy.sentTo.length > 0 ? ` to ${shown.copy.sentTo.join(", ")}` : " (link only)"}
                  {shown.copy.inHandsDate ? ` · in-hands ${shown.copy.inHandsDate}` : ""}
                </p>
                <table className="mt-3 w-full">
                  <thead className="border-b border-brown-100 text-left text-[11px] font-bold uppercase tracking-wide text-brown-300">
                    <tr><th className="py-2">Item</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Unit price</th></tr>
                  </thead>
                  <tbody>
                    {shown.copy.lines.map((l) => (
                      <tr key={l.id} className="border-b border-brown-50 align-top">
                        <td className="py-2">
                          <span className="font-semibold">{l.productSnapshot?.name || l.description || "Item"}</span>
                          {(l.variants ?? []).length > 0 && (
                            <span className="block text-xs text-brown-300">
                              {(l.variants ?? []).map((v) => `${[v.color, v.size].filter(Boolean).join(" / ") || "—"} × ${v.quantity}${v.unitPrice ? ` @ ${money(v.unitPrice)}` : ""}`).join(" · ")}
                            </span>
                          )}
                          {(l.decorations ?? []).flatMap((d) => d.charges ?? []).map((c, i) => (
                            <span key={i} className="block text-xs text-brown-300">
                              {c.note || c.chargeType}: {money(c.amount)}{c.quantity != null && c.quantity !== 1 ? ` × ${c.quantity}` : c.billingModel === "per_piece" ? " each" : ""}
                            </span>
                          ))}
                        </td>
                        <td className="py-2 text-right tabular-nums">{l.serviceType === "shipping" ? "" : (l.quantityTotal ?? 0).toLocaleString()}</td>
                        <td className="py-2 text-right tabular-nums">{shown.copy.hidePricing ? "—" : money(l.unitPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {shown.copy.totals && (
                  <dl className="ml-auto mt-3 w-64 space-y-1 text-sm tabular-nums">
                    <div className="flex justify-between"><dt className="text-brown-300">Subtotal</dt><dd>{money(shown.copy.totals.subtotal)}</dd></div>
                    <div className="flex justify-between"><dt className="text-brown-300">Tax</dt><dd>{money(shown.copy.totals.tax)}</dd></div>
                    <div className="flex justify-between border-t border-brown-100 pt-1 font-semibold"><dt>Total</dt><dd>{money(shown.copy.totals.total)}</dd></div>
                  </dl>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
